import { createQueryString } from "@/service/api-service";
import { type AppRouterProgressInstance } from "@bprogress/next";
import { clsx, type ClassValue } from "clsx";
import { Dispatch, SetStateAction } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function addNonBreakingSpacesAfterConjunctions(text: string) {
  if (!text) {
    return "";
  }
  const conjunctionsRegex =
    /\b(i|I|II|III|a|oraz|czyli|lub|albo|bo|ponieważ|gdyż|jednak|lecz|ale|więc|zatem)\b /g;

  return text.replace(conjunctionsRegex, "$1&nbsp;");
}

export function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export function parseIntOrNull(
  value: string | undefined | null,
): number | null {
  if (value === undefined || value === null) {
    return null;
  }

  const parsedValue = parseInt(value, 10);
  return isNaN(parsedValue) ? null : parsedValue;
}

export function parseIntOrDefault(
  value: string | undefined | null,
  defaultValue: number,
): number {
  if (value === undefined || value === null) {
    return defaultValue;
  }

  const parsedValue = parseInt(value, 10);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
}

export function parseSearchParamsChainOrNull(
  value: string | null | undefined,
): string[] | null {
  if (value === null || value === undefined) {
    return null;
  }
  const parsedValue = value.split(",");
  return parsedValue.length > 0 ? parsedValue : null;
}

export function updateSearchParams(
  key: string,
  value: string | string[] | null,
  router: AppRouterProgressInstance | NextRouter,
) {
  const searchParams = new URLSearchParams(window.location.search);
  if (value === null || value.length < 1) {
    searchParams.delete(key);
  } else {
    if (Array.isArray(value)) {
      searchParams.set(key, value.map((v) => String(v)).toString());
    } else {
      searchParams.set(key, String(value));
    }
  }

  const newPathname = location.pathname + `?` + createQueryString(searchParams);
  router.push(newPathname, {
    scroll: false,
  });
}

// AI generated
export function extractSearchParamsFromString(
  link: string,
): Record<string, string | string[]> {
  const query = link.split("?")[1] || ""; // część po "?"
  return query
    .split("&") // "a=1&b=2"
    .filter(Boolean) // usuń puste fragmenty
    .reduce<Record<string, string | string[]>>((acc, pair) => {
      const [rawKey = "", rawVal = ""] = pair.split("=");
      const key = decodeURIComponent(rawKey);
      const val = decodeURIComponent(rawVal);

      if (!key) return acc; // pomiń brak klucza

      if (acc[key] !== undefined) {
        // jeżeli klucz już istnieje => zamień/rozszerz na tablicę wartości
        acc[key] = Array.isArray(acc[key])
          ? [...(acc[key] as string[]), val]
          : [acc[key] as string, val];
      } else {
        acc[key] = val; // pierwszy raz – po prostu string
      }
      return acc;
    }, {});
}

export function parseInputValueToFloatAndUpdateSetter(
  value: string,
  setter: Dispatch<SetStateAction<number | string>>,
) {
  if (!value || value === "") {
    setter("");
    return 0;
  }

  if (value.trim().endsWith(".") || value.trim().endsWith(",")) {
    setter(value.replace(",", "."));
    return value;
  }

  if (isNaN(parseFloat(value))) {
    setter(0);
    return 0;
  }

  setter(parseFloat(value));
  return parseFloat(value);
}

export function calculateTimeLeft(targetDate: Date) {
  const now = new Date();
  const difference = targetDate.getTime() - now.getTime();

  if (difference <= 0) {
    return {
      months: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  const months = Math.floor(difference / (1000 * 60 * 60 * 24 * 30));
  const days = Math.floor(
    (difference % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24),
  );
  const hours = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { months, days, hours, minutes, seconds };
}
